import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import { TsicommonCmsCompareFeatureOrNull } from "@reactivated";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { DEFAULT_MATTRESS_SIZE_FOR_COMPARE } from "../../constants";
import { CompareTileType } from "../../models/shop-compare";
import { GroupedTileModelsType } from "../../templates/ShopTileTemplate";
import { getOptionValueHash } from "../../utils/catalogue";
import { useCurrentBreakpoint } from "../../utils/hooks";
import { usePageSetting } from "../../utils/settings";
import { resolveTileVariant } from "../../utils/shopCompare";
import { sortProductOptions } from "../../utils/sorting";
import { useIsSSR } from "../../utils/ssr";
import { CompareFeatures } from "../CompareFeatures";
import { CompareTile } from "../CompareTile";
import { BlankCompareTile } from "../CompareTile/BlankCompareTile";
import { Link } from "../Link";
import { ModelSelector } from "../ModelSelector";

import styles from "./index.module.scss";

interface ICompareGridProps {
    compareTiles: CompareTileType[];
    compareFeatures: TsicommonCmsCompareFeatureOrNull[];
    handleSelectProduct: (newTile: number, column: number) => void;
    selectedProducts: number[];
    groupedDropdownModels: GroupedTileModelsType;
    initialSize?: string | null;
}

export const CompareGrid = (props: ICompareGridProps) => {
    const isSSR = useIsSSR();
    const currentBreakpoint = useCurrentBreakpoint();
    const ecomEnabled = !!usePageSetting("enable_ecom");
    const isBelowLaptop = !isSSR && currentBreakpoint.belowLaptop;
    const enableEcom = usePageSetting("enable_ecom");

    const [selectedCompareTiles, setSelectedCompareTiles] = useState<
        (CompareTileType | null)[]
    >([]);

    const maxCompareTiles = isBelowLaptop ? 2 : 3;

    const selectedProducts = useMemo(
        () => props.selectedProducts?.slice(0, maxCompareTiles),
        [props.selectedProducts, maxCompareTiles],
    );

    const allSizeOptions = props.compareTiles
        .map((tile) => tile.size_options)
        .filter((arr): arr is string[] => Array.isArray(arr));

    const availableSizes = (() => {
        if (allSizeOptions.length === 0) return [] as string[];
        const allSizes = allSizeOptions.reduce((acc, curr) => {
            return [...acc, ...curr];
        }, [] as string[]);
        const commonSizes = [...new Set(allSizes)];
        return sortProductOptions("option_size", commonSizes);
    })();

    const defaultSelected = (() => {
        if (availableSizes.length === 0) return "";
        const fromUrl =
            props.initialSize &&
            availableSizes.includes(props.initialSize) &&
            props.initialSize;
        if (fromUrl) return fromUrl;
        return availableSizes.includes(DEFAULT_MATTRESS_SIZE_FOR_COMPARE)
            ? DEFAULT_MATTRESS_SIZE_FOR_COMPARE
            : availableSizes[0];
    })();

    const [selectedSize, setSelectedSize] = useState<string>(defaultSelected);

    useEffect(() => {
        if (selectedProducts) {
            const uniqueProductIds = Array.from(new Set(selectedProducts));
            const uniqueTiles = uniqueProductIds.map(
                (productId) =>
                    props.compareTiles.find((t) => t.product === productId) ||
                    null,
            );
            setSelectedCompareTiles(uniqueTiles);
            return;
        }
    }, [selectedProducts, props.compareTiles]);

    useEffect(() => {
        setSelectedSize(defaultSelected);
    }, [defaultSelected]);

    const selectID = "compare-size-selector";
    const onSubmit = (e: React.FormEvent<HTMLFormElement>) =>
        e.preventDefault();
    const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSize(e.target.value);
    };

    const selectedDisplayTiles: (CompareTileType | null)[] = (() => {
        if (selectedCompareTiles.length < maxCompareTiles) {
            const blankTiles = Array(
                maxCompareTiles - selectedCompareTiles.length,
            ).fill(null);
            return [...selectedCompareTiles, ...blankTiles];
        }
        return selectedCompareTiles.slice(0, maxCompareTiles);
    })();

    const sizeSelector =
        availableSizes.length > 0 ? (
            <form className={styles.sizeSelector} onSubmit={onSubmit}>
                <label htmlFor={selectID}>{t`Price by mattress size`}</label>
                <span className={styles.selectWrapper}>
                    <select
                        id={selectID}
                        aria-label={t`Select mattress size`}
                        className="al-compareresults-selectsize"
                        onChange={onSelectChange}
                        value={selectedSize}
                    >
                        {availableSizes.map((option) => (
                            <option
                                key={getOptionValueHash(option)}
                                value={option}
                            >
                                {option}
                            </option>
                        ))}
                    </select>
                </span>
            </form>
        ) : null;

    return (
        <div>
            <div className={styles.headerContainer}>
                <h1>{t`Compare All Mattress Models`}</h1>
                {ecomEnabled && !isBelowLaptop && sizeSelector}
            </div>
            {ecomEnabled && isBelowLaptop && sizeSelector}
            <div className={styles.stickyModelSelector}>
                <div className={styles.stickyModelSelectorContainer}>
                    {selectedDisplayTiles.map((tile, index) =>
                        tile == null ? (
                            <ModelSelector
                                id="compare_tile_model_empty"
                                key={`compare_tile_model_${index}`}
                                label="Select Model"
                                value={null}
                                onChange={(newTile: number) => {
                                    props.handleSelectProduct(newTile, index);
                                }}
                                groupedModels={props.groupedDropdownModels}
                                selectedProducts={selectedProducts}
                                placeholder="Select Model"
                            />
                        ) : (
                            <ModelSelector
                                id={`compare_tile_model_${tile.product}`}
                                key={`compare_tile_model_${index}`}
                                label={tile.title || ""}
                                value={tile.product || null}
                                onChange={(newTile: number) =>
                                    props.handleSelectProduct(newTile, index)
                                }
                                groupedModels={props.groupedDropdownModels}
                                selectedProducts={selectedProducts}
                            />
                        ),
                    )}
                </div>
            </div>
            <div className={styles.compareTileContainer}>
                <div className={styles.compareTiles}>
                    {selectedDisplayTiles.map((tile, index) =>
                        tile === null ? (
                            <BlankCompareTile
                                key={`blank-${index}`}
                                handleSelectProduct={(newTile: number) =>
                                    props.handleSelectProduct(newTile, index)
                                }
                                selectedProducts={selectedProducts}
                                groupedDropdownModels={
                                    props.groupedDropdownModels
                                }
                            />
                        ) : (
                            <CompareTile
                                key={`tile-${tile.id || index}`}
                                tile={tile}
                                handleSelectProduct={(newTile: number) =>
                                    props.handleSelectProduct(newTile, index)
                                }
                                selectedProducts={selectedProducts}
                                selectedSize={selectedSize}
                                groupedDropdownModels={
                                    props.groupedDropdownModels
                                }
                            />
                        ),
                    )}
                </div>
            </div>
            <div className={styles.compareFeatures}>
                <CompareFeatures
                    features={props.compareFeatures}
                    compareTileFeatures={selectedDisplayTiles.map(
                        (tile) =>
                            tile?.compare_features?.map((cf) => cf.value) || [],
                    )}
                />
                <div className={styles.shopNowButtonContainer}>
                    {selectedDisplayTiles.map((tile, index) =>
                        tile !== null ? (
                            (() => {
                                const variant = resolveTileVariant(
                                    tile,
                                    selectedSize,
                                );
                                return variant !== null ? (
                                    <Link
                                        key={`shop-now-button-compare-footer-${index}`}
                                        href={variant.link}
                                        className="button al-comparisongrid-shopnow"
                                        id={`shop-now-button-compare-footer-${index}`}
                                    >
                                        {enableEcom ? t`Shop Now` : t`Explore`}
                                        <span className="ada-screenreader-only">
                                            {interpolate(
                                                t`for %(productName)s`,
                                                {
                                                    productName:
                                                        tile.title || "",
                                                },
                                            )}
                                        </span>
                                    </Link>
                                ) : (
                                    <button
                                        key={`shop-now-button-compare-footer-${index}`}
                                        className="button al-comparisongrid-shopnow"
                                        aria-label={interpolate(
                                            t`%(productName)s is unavailable in the selected size`,
                                            {
                                                productName: tile.title || "",
                                            },
                                        )}
                                        disabled
                                    >
                                        {t`Unavailable`}
                                    </button>
                                );
                            })()
                        ) : (
                            <span
                                key={`shop-now-button-compare-footer-${index}`}
                            ></span>
                        ),
                    )}
                </div>
            </div>
        </div>
    );
};
